import Navigation from "@/components/Navigation";
import { EnterpriseROICalculator } from "@/components/EnterpriseROICalculator";
import FooterWithNewsletter from "@/components/ui/footer-column";
import FinalCTA from "@/components/FinalCTA";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const ROICalculatorPage = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  // Parallax transforms for different layers
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y4 = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>ROI Calculator - Calculate Your AI Content Savings | gimmefy</title>
        <meta 
          name="description" 
          content="Calculate your team's potential time and cost savings with AI-powered content creation. See how gimmefy can transform your marketing team's efficiency and ROI." 
        />
        <meta 
          name="keywords" 
          content="ROI calculator, AI content savings, marketing efficiency, content creation ROI, enterprise marketing tools, AI marketing calculator" 
        />
      </Helmet>

      <Navigation />

      {/* ROI Calculator Section */}
      <section 
        ref={sectionRef}
        className="relative py-12 md:py-16 overflow-hidden"
      >
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/80 via-cyan-50/60 to-teal-50/80 dark:from-emerald-950/30 dark:via-cyan-950/20 dark:to-teal-950/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_20%_20%,rgba(16,185,129,0.12),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_20%_20%,rgba(16,185,129,0.08),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_80%,rgba(6,182,212,0.1),transparent)] dark:bg-[radial-gradient(ellipse_60%_40%_at_80%_80%,rgba(6,182,212,0.06),transparent)]" />

        {/* Animated floating shapes with parallax */}
        {/* Shape 1 - Large circle top left */}
        <motion.div
          style={{ y: y1 }}
          className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-gradient-to-br from-emerald-400/10 to-cyan-400/5 blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            x: [0, 20, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Shape 2 - Medium rounded rect top right */}
        <motion.div
          style={{ y: y2 }}
          className="absolute top-40 -right-10 w-64 h-64 rounded-[40px] rotate-12 bg-gradient-to-tl from-cyan-400/8 to-emerald-400/5 blur-2xl"
          animate={{
            rotate: [12, 18, 12],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Shape 3 - Small circle mid left */}
        <motion.div
          style={{ y: y3 }}
          className="absolute top-1/2 left-10 w-40 h-40 rounded-full bg-gradient-to-r from-teal-400/12 to-emerald-400/6 blur-2xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 15, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Shape 4 - Large blob bottom right */}
        <motion.div
          style={{ y: y4 }}
          className="absolute bottom-20 right-1/4 w-72 h-72 rounded-full bg-gradient-to-bl from-emerald-400/8 to-cyan-400/10 blur-3xl"
          animate={{
            scale: [1, 1.15, 1],
            y: [0, 25, 0],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Shape 5 - Small accent bottom left */}
        <motion.div
          style={{ y: y2 }}
          className="absolute bottom-40 left-1/4 w-32 h-32 rounded-[30px] -rotate-6 bg-gradient-to-tr from-cyan-400/10 to-teal-400/5 blur-xl"
          animate={{
            rotate: [-6, 6, -6],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Content */}
        <div className="relative z-10">
          <EnterpriseROICalculator />
        </div>
      </section>

      <FinalCTA 
        headline={<>Ready to Make<br />These Savings Real?</>}
        subheadline="Let's build your ROI roadmap"
        bodyText={<>You've seen the numbers. Now let's make them happen.<br /><br />In one working session, we'll map how gimmefy Fusion fits into your current workflow and identify where you'll capture the biggest wins.</>}
      />

      <FooterWithNewsletter />
    </div>
  );
};

export default ROICalculatorPage;
